/**
 * SocketProxy wraps a Socket.IO socket instance and forwards all calls.
 * When the underlying socket is swapped (on reconnect), all existing
 * `.on()` listeners are re-registered on the new socket automatically.
 * This keeps the UI working across reconnects without rebuilding it.
 *
 * Ported from fe/src/lib/socketProxy.js, typed for the support widget.
 */

export type SocketLike = {
  on: (event: string, handler: (...args: any[]) => void) => unknown;
  off: (event: string, handler: (...args: any[]) => void) => unknown;
  once: (event: string, handler: (...args: any[]) => void) => unknown;
  emit: (...args: any[]) => unknown;
  connected?: boolean;
  id?: string;
};

type Listener = { event: string; handler: (...args: any[]) => void };

export class SocketProxy {
  private _socket: SocketLike;
  private _listeners: Listener[] = [];

  constructor(socket: SocketLike) {
    this._socket = socket;
  }

  /** Swap the underlying socket (e.g. after reconnect) */
  swap(newSocket: SocketLike): void {
    for (const { event, handler } of this._listeners) {
      this._socket.off(event, handler);
    }
    this._socket = newSocket;
    for (const { event, handler } of this._listeners) {
      this._socket.on(event, handler);
    }
  }

  /** Register an event listener — tracked for re-registration on swap */
  on(event: string, handler: (...args: any[]) => void): this {
    this._listeners.push({ event, handler });
    this._socket.on(event, handler);
    return this;
  }

  /** Remove an event listener */
  off(event: string, handler: (...args: any[]) => void): this {
    this._listeners = this._listeners.filter(
      (l) => !(l.event === event && l.handler === handler)
    );
    this._socket.off(event, handler);
    return this;
  }

  /** Register a one-time listener (not re-registered on swap) */
  once(event: string, handler: (...args: any[]) => void): this {
    this._socket.once(event, handler);
    return this;
  }

  /** Emit an event to the server */
  emit(...args: any[]): this {
    this._socket.emit(...args);
    return this;
  }

  get connected(): boolean {
    return Boolean(this._socket.connected);
  }

  get id(): string | undefined {
    return this._socket.id;
  }
}
